<script lang="ts" setup>
import type { VoixWysiwygFieldInterface } from '@voix/components/fields/wysiwyg/types'
import { toTypedSchema } from '@vee-validate/zod'
import { ErrorMessage, Field, Form } from 'vee-validate'
import * as zod from 'zod'

defineProps<{
  fields: {
    description: VoixWysiwygFieldInterface
  }
}>()

defineSlice({
  name: { label: 'Contact Form', group: 'Forms', layouts: ['VoixMarketing'] },
  description: 'A contact form for users to get in touch with the site owner.',
  preview: 'SlicesFormsContactForm.jpg',
  fields: {
    description: {
      type: 'wysiwyg',
      label: 'Description',
      default: '<p>We look forward to learning about your project. You can expect a response usually same day Monday - Friday Eastern Standard Time.</p>',
    },
  },
  templates: [{
    label: 'Main Example',
    fields: {
      validationSchema: {
        firstName: 'string',
        lastName: 'string',
        email: 'string',
        phoneNumber: 'string',
      },
    },
  }],
})

const phoneRegex = /^\+?(\d[\d\s-]{7,}\d)$/

const validationSchema = toTypedSchema(
  zod.object({
    firstName: zod.string().min(1, 'First name is required'),
    lastName: zod.string().min(1, 'Last name is required'),
    email: zod.string().min(1, 'Email is required').email({ message: 'Must be a valid email' }),
    phoneNumber: zod.string().min(9, 'Phone Number is required').regex(phoneRegex, { message: 'Must be a valid phone number' }),
  }),
)

const form = reactive({
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  message: '',
  complete: false,
})

async function submitContact() {
  await $fetch('/api/contact-form', {
    method: 'POST',
    body: JSON.stringify(form),
  }).then(() => {
    form.firstName = ''
    form.lastName = ''
    form.email = ''
    form.phoneNumber = ''
    form.message = ''
    form.complete = true
  }).catch(err => console.error(err))
}
</script>

<template>
  <div id="contact" class="relative isolate bg-voix-900">
    <div class="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2">
      <div class="relative px-6 pb-20 pt-24 sm:pt-32 lg:static lg:px-8 lg:py-48">
        <div class="mx-auto max-w-xl lg:mx-0 lg:max-w-lg">
          <div class="absolute inset-y-0 left-0 -z-10 w-full overflow-hidden ring-1 ring-white/5 lg:w-1/2">
            <svg class="absolute inset-0 h-full w-full stroke-gray-700 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]" aria-hidden="true">
              <defs>
                <pattern id="54f88622-e7f8-4f1d-aaf9-c2f5e46dd1f2" width="200" height="200" x="100%" y="-1" patternUnits="userSpaceOnUse">
                  <path d="M130 200V.5M.5 .5H200" fill="none" />
                </pattern>
              </defs>
              <svg x="100%" y="-1" class="overflow-visible fill-gray-800/20">
                <path d="M-470.5 0h201v201h-201Z" stroke-width="0" />
              </svg>
              <rect width="100%" height="100%" stroke-width="0" fill="url(#54f88622-e7f8-4f1d-aaf9-c2f5e46dd1f2)" />
            </svg>
            <div class="absolute -left-56 top-[calc(100%-13rem)] transform-gpu blur-3xl lg:left-[max(-14rem,calc(100%-59rem))] lg:top-[calc(50%-7rem)]" aria-hidden="true">
              <div class="aspect-[1155/678] w-[72.1875rem] bg-gradient-to-br from-[#FFFD5A] to-[#3805F5] opacity-20" style="clip-path: polygon(74.1% 56.1%, 100% 38.6%, 97.5% 73.3%, 85.5% 100%, 80.7% 98.2%, 72.5% 67.7%, 60.2% 37.8%, 52.4% 32.2%, 47.5% 41.9%, 45.2% 65.8%, 27.5% 23.5%, 0.1% 35.4%, 17.9% 0.1%, 27.6% 23.5%, 76.1% 2.6%, 74.1% 56.1%)" />
            </div>
          </div>
          <h2 class="heading-2 border-b border-voix-stroke pb-3">
            Get in touch
          </h2>
          <VoixWysiwyg :field="fields.description" class="mt-5 heading-6" />
          <dl class="mt-10 space-y-4 text-base leading-7 text-gray-300">
            <div class="flex gap-x-4">
              <dt class="flex-none">
                <span class="sr-only">Telephone</span>
                <Icon name="heroicons:phone-20-solid" class="h-7 w-6 text-gray-400" aria-hidden="true" />
              </dt>
              <dd class="font-sans">
                <a class="hover:text-white" href="tel:+12293038649">+1 (229) 303-VOIX (8649)</a>
              </dd>
            </div>
            <div class="flex gap-x-4">
              <dt class="flex-none">
                <span class="sr-only">Email</span>
                <Icon name="heroicons:envelope-20-solid" class="h-7 w-6 text-gray-400" aria-hidden="true" />
              </dt>
              <dd class="font-sans">
                <a class="hover:text-white" href="mailto:info@voix.ooo">info@voix.ooo</a>
              </dd>
            </div>
          </dl>
        </div>
      </div>
      <Form v-if="!form.complete" :validation-schema="validationSchema" class="font-sans px-6 pb-24 pt-20 sm:pb-32 lg:px-8 lg:py-48" @submit="submitContact">
        <div class="mx-auto max-w-xl lg:mr-0 lg:max-w-lg">
          <div class="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
            <div>
              <div class="flex justify-between items-end">
                <label for="first-name" class="block text-sm font-light leading-6 text-white">First name*</label>
                <div class="uppercase text-slate-500 text-[10px] font-sans font-thin">
                  Required
                </div>
              </div>
              <div class="mt-2">
                <Field id="first-name" v-model="form.firstName" type="text" name="firstName" autocomplete="given-name" class="block w-full rounded-md border-0 bg-white/5 px-3.5 py-2 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6" />
                <ErrorMessage class="pt-1 text-sm text-red-400" name="firstName" />
              </div>
            </div>
            <div>
              <div class="flex justify-between items-end">
                <label for="last-name" class="block text-sm font-light leading-6 text-white">Last name*</label>
                <div class="uppercase text-slate-500 text-[10px] font-sans font-thin">
                  Required
                </div>
              </div>
              <div class="mt-2">
                <Field id="last-name" v-model="form.lastName" type="text" name="lastName" autocomplete="family-name" class="block w-full rounded-md border-0 bg-white/5 px-3.5 py-2 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6" />
                <ErrorMessage class="pt-1 text-sm text-red-400" name="lastName" />
              </div>
            </div>
            <div class="sm:col-span-2">
              <div class="flex justify-between items-end">
                <label for="email" class="block text-sm font-light leading-6 text-white">Email*</label>
                <div class="uppercase text-slate-500 text-[10px] font-sans font-thin">
                  Required
                </div>
              </div>
              <div class="mt-2">
                <Field id="email" v-model="form.email" type="email" name="email" autocomplete="email" class="block w-full rounded-md border-0 bg-white/5 px-3.5 py-2 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6" />
                <ErrorMessage class="pt-1 text-sm text-red-400" name="email" />
              </div>
            </div>
            <div class="sm:col-span-2">
              <div class="flex justify-between items-end">
                <label for="phone-number" class="block text-sm font-light leading-6 text-white">Phone number*</label>
                <div class="uppercase text-slate-500 text-[10px] font-sans font-thin">
                  Required
                </div>
              </div>
              <div class="mt-2">
                <Field id="phone-number" v-model="form.phoneNumber" type="tel" name="phoneNumber" autocomplete="tel" class="block w-full rounded-md border-0 bg-white/5 px-3.5 py-2 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6" />
                <ErrorMessage class="pt-1 text-sm text-red-400" name="phoneNumber" />
              </div>
            </div>
            <div class="sm:col-span-2">
              <label for="message" class="block text-sm font-light leading-6 text-white">Message (optional)</label>
              <div class="mt-2.5">
                <textarea id="message" v-model="form.message" name="message" rows="4" class="block w-full rounded-md border-0 bg-white/5 px-3.5 py-2 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6" />
              </div>
            </div>
          </div>
          <div class="mt-8 flex justify-end">
            <InterfaceButtons type="submit" reveal-color="bg-teal-500" to="/sales" size="large" class="border border-zinc-500 pointer-events-auto">
              Send Message
              <template #revealText>
                😉
              </template>
            </InterfaceButtons>
          </div>
        </div>
      </Form>

      <div v-else class="px-6 pb-24 pt-20 sm:pb-32 lg:px-8 lg:py-56 text-center text-3xl">
        Form submitted successfully! We will be in touch as soon as possible!
      </div>
    </div>
  </div>
</template>
